import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FormData from 'form-data';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Plus as PlusIcon } from '../../assets/icons/plus';
import { styles } from './styles';
import CustomTable from '../../components/common/CustomTable';
import { columns } from './columns';
import PermissionChecker from '../../components/common/PermissionChecker';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
    setInitialFilters,
} from '../../store/slices/tableController';
import { CompanyFilter } from './CompanyFilter';
import { useSearchCompaniesMutation, useListCompaniesMutation } from '../../api/company';

const Company = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const controller = useSelector((state) => state.tableController);
    const [companies, setCompanies] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    const [searchCompanies, { isLoading: isSearchLoading }] = useSearchCompaniesMutation();
    const [listCompanies, { isLoading: isListLoading }] = useListCompaniesMutation();

    const loading = isSearchLoading || isListLoading;

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                if (searched) {
                    const query = new URLSearchParams({});
                    if (filters.length) {
                        filters.forEach((filter) => query.append(filter.property, filter.value));
                    }
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await searchCompanies(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setCompanies(response);
                } else {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await listCompanies(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setCompanies(response);
                }
            } catch (error) {
                if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
                else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchCompanies, listCompanies, dispatch, enqueueSnackbar],
    );

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'companyName',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleRowClick = (id) => {
        navigate(`/company/${id}`);
    };

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Company</Typography>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_CREATE_COMPANY']}
                    noAuthRedirect={false}
                >
                    <Button
                        color="primary"
                        onClick={() => navigate('/company/add')}
                        size="large"
                        startIcon={<PlusIcon fontSize="small" />}
                        variant="contained"
                        sx={{
                            ':hover': {
                                backgroundColor: 'secondary.main',
                            },
                        }}
                    >
                        Add Company
                    </Button>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <CompanyFilter
                            disabled={false}
                            initialFilters={controller.initialFilters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            filterProperties={columns}
                            filters={controller.filters}
                        />
                        <CustomTable
                            isLoading={loading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onRowClick={handleRowClick}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={companies.companyInstanceList}
                            rowsCount={companies.companyInstanceCount}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={false}
                            showPagignation={true}
                            keyColumn="id"
                            action={() =>
                                fetchData(
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                )
                            }
                        />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default Company;
